/** Admin → Location Service → Warehouses & stock: fulfilment centres (and Phase 2 dark stores), their zones, and
 * per-location stock — available, reserved for open orders, damaged, reorder level, bin (pick sequence). */
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, History, PackagePlus, Pencil, Plus, Search, Trash2, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { useLse } from "../../../lib/lse";
import { Btn, Empty, ErrorBox, Field, Loading, Modal, PageHead, Pill, ResponsiveList, Toggle, errText, inputCls } from "./LseUI";

const KINDS = { WAREHOUSE: "Warehouse", FULFILMENT_CENTRE: "Fulfilment centre", HUB: "Local hub", DARK_STORE: "Dark store" };
const EMPTY = { name: "", code: "", kind: "WAREHOUSE", address: "", lat: "", lng: "", zone_ids: [], daily_order_capacity: "", service_radius_km: "", open_time: "", close_time: "", max_active_orders: "", active: true };
const num = (v) => (v === "" || v == null ? null : Number(v));

export default function LseWarehouses() {
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState(null);
    const [zones, setZones] = useState([]);
    const [edit, setEdit] = useState(null);
    const [stockOf, setStockOf] = useState(null);
    const [low, setLow] = useState([]);
    const load = () => {
        setErr(null);
        api.get("/admin/lse/warehouses").then(({ data }) => setRows(data.warehouses || [])).catch((e) => setErr(errText(e)));
        api.get("/admin/lse/stock/low").then(({ data }) => setLow(data.items || [])).catch(() => {});
    };
    useEffect(() => {
        load();
        api.get("/admin/zones").then(({ data }) => setZones((data || []).filter((z) => z.status !== "DELETED"))).catch(() => {});
    }, []);
    const remove = async (w) => {
        if (!window.confirm(`Remove ${w.name}? It must have no open orders.`)) return;
        try { await api.delete(`/admin/lse/warehouses/${w.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(errText(e)); }
    };

    return (
        <div data-testid="lse-warehouses">
            <PageHead title="Warehouses & stock" sub="Where orders are fulfilled from. A product with stock rows here is sold only where a serving warehouse has it; checkout reserves stock and delivery ships it."
                      action={<Btn onClick={() => setEdit(EMPTY)} data-testid="lse-wh-add"><Plus size={16} /> Add warehouse</Btn>} />
            {low.length > 0 && (
                <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-800 p-4 text-sm" data-testid="lse-low-stock">
                    <p className="font-semibold inline-flex items-center gap-2"><AlertTriangle size={16} /> {low.length} item(s) at or below their reorder level</p>
                    <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                        {low.slice(0, 8).map((r) => <li key={`${r.warehouse_id}-${r.product_id}`} className="truncate">{r.product_name} · {r.warehouse_name}: {r.sellable} left (reorder at {r.reorder_level})</li>)}
                    </ul>
                </div>
            )}
            {err && <ErrorBox message={err} onRetry={load} />}
            {!rows && !err && <Loading />}
            {rows?.length === 0 && <Empty>No warehouses yet. Add your first fulfilment centre.</Empty>}
            {rows?.length > 0 && (
                <ResponsiveList testid="lse-wh-list" rows={rows}
                    columns={[
                        { key: "name", label: "Warehouse", render: (w) => <span><span className="font-semibold">{w.name}</span>{w.code ? <span className="text-muted-foreground"> · {w.code}</span> : null}</span> },
                        { key: "kind", label: "Kind", render: (w) => <Pill tone={w.kind === "DARK_STORE" ? "blue" : "muted"}>{KINDS[w.kind]}</Pill> },
                        { key: "zones", label: "Zones", render: (w) => (w.zone_ids || []).map((z) => zones.find((x) => x.id === z)?.name || "…").join(", ") || "—" },
                        { key: "stock", label: "Stock", render: (w) => `${w.sku_count} products${w.low_stock_count ? ` · ${w.low_stock_count} low` : ""}` },
                        { key: "open", label: "Open orders", render: (w) => w.open_orders },
                        { key: "a", label: "", className: "text-right", render: (w) => <RowActions w={w} onStock={() => setStockOf(w)} onEdit={() => setEdit(w)} onDelete={() => remove(w)} /> },
                    ]}
                    card={(w) => (
                        <div>
                            <div className="flex items-center justify-between gap-2"><p className="font-semibold truncate">{w.name}</p><Pill tone={w.kind === "DARK_STORE" ? "blue" : "muted"}>{KINDS[w.kind]}</Pill></div>
                            <p className="text-xs text-muted-foreground mt-1">{w.sku_count} products · {w.open_orders} open orders{w.low_stock_count ? ` · ${w.low_stock_count} low` : ""}</p>
                            <div className="mt-2"><RowActions w={w} onStock={() => setStockOf(w)} onEdit={() => setEdit(w)} onDelete={() => remove(w)} /></div>
                        </div>
                    )}
                />
            )}
            {edit && <WarehouseForm initial={edit} zones={zones} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
            {stockOf && <StockDrawer wh={stockOf} onClose={() => { setStockOf(null); load(); }} />}
        </div>
    );
}

function RowActions({ onStock, onEdit, onDelete }) {
    return (
        <span className="inline-flex gap-2 flex-wrap">
            <Btn onClick={onStock} className="!px-3"><Warehouse size={15} /> Stock</Btn>
            <Btn tone="ghost" onClick={onEdit} className="!px-3" aria-label="Edit"><Pencil size={15} /></Btn>
            <Btn tone="ghost" onClick={onDelete} className="!px-3" aria-label="Remove"><Trash2 size={15} /></Btn>
        </span>
    );
}

function WarehouseForm({ initial, zones, onClose, onSaved }) {
    const { has } = useLse();
    const [f, setF] = useState({ ...EMPTY, ...initial, lat: initial.lat ?? "", lng: initial.lng ?? "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
    const save = async () => {
        if (!f.name.trim()) return toast.error("Give it a name");
        setBusy(true);
        const body = {
            name: f.name.trim(), code: f.code || null, kind: f.kind, address: f.address || null, lat: num(f.lat), lng: num(f.lng),
            zone_ids: f.zone_ids, daily_order_capacity: num(f.daily_order_capacity), service_radius_km: num(f.service_radius_km),
            open_time: f.open_time || null, close_time: f.close_time || null, max_active_orders: num(f.max_active_orders), active: !!f.active,
        };
        try {
            if (initial.id) await api.patch(`/admin/lse/warehouses/${initial.id}`, body); else await api.post("/admin/lse/warehouses", body);
            toast.success("Saved"); onSaved();
        } catch (e) { toast.error(errText(e, "Check the values")); } finally { setBusy(false); }
    };
    return (
        <Modal title={initial.id ? `Edit ${initial.name}` : "New warehouse"} onClose={onClose}
               footer={<><Btn tone="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy} data-testid="lse-wh-save">Save</Btn></>}>
            <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Name"><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="lse-wh-name" /></Field>
                <Field label="Code"><input className={inputCls} value={f.code || ""} onChange={(e) => set("code", e.target.value)} /></Field>
                <Field label="Kind">
                    <select className={inputCls} value={f.kind} onChange={(e) => set("kind", e.target.value)}>
                        {Object.entries(KINDS).filter(([k]) => k !== "DARK_STORE" || has("dark_store") || f.kind === k).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </Field>
                <Field label="Orders per day (capacity)"><input className={inputCls} type="number" min="1" value={f.daily_order_capacity ?? ""} onChange={(e) => set("daily_order_capacity", e.target.value)} /></Field>
                <Field label="Address" full><input className={inputCls} value={f.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
                <Field label="Latitude"><input className={inputCls} type="number" step="any" value={f.lat} onChange={(e) => set("lat", e.target.value)} /></Field>
                <Field label="Longitude"><input className={inputCls} type="number" step="any" value={f.lng} onChange={(e) => set("lng", e.target.value)} /></Field>
                <Field label="Serves zones" full>
                    <div className="flex flex-wrap gap-2">
                        {zones.map((z) => (
                            <button key={z.id} type="button" onClick={() => set("zone_ids", f.zone_ids.includes(z.id) ? f.zone_ids.filter((x) => x !== z.id) : [...f.zone_ids, z.id])}
                                    className={`rounded-full px-3 py-1 text-xs font-semibold border ${f.zone_ids.includes(z.id) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{z.name}</button>
                        ))}
                        {zones.length === 0 && <span className="text-xs text-muted-foreground">No zones yet</span>}
                    </div>
                </Field>
                {f.kind === "DARK_STORE" && (
                    <>
                        <Field label="Instant radius (km)"><input className={inputCls} type="number" min="0.1" step="0.1" value={f.service_radius_km ?? ""} onChange={(e) => set("service_radius_km", e.target.value)} /></Field>
                        <Field label="Max open orders"><input className={inputCls} type="number" min="1" value={f.max_active_orders ?? ""} onChange={(e) => set("max_active_orders", e.target.value)} /></Field>
                        <Field label="Opens"><input className={inputCls} type="time" value={f.open_time || ""} onChange={(e) => set("open_time", e.target.value)} /></Field>
                        <Field label="Closes"><input className={inputCls} type="time" value={f.close_time || ""} onChange={(e) => set("close_time", e.target.value)} /></Field>
                    </>
                )}
                <Field label="Status"><Toggle checked={f.active} onChange={(v) => set("active", v)} label="Active" /></Field>
            </div>
        </Modal>
    );
}

function StockDrawer({ wh, onClose }) {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [adj, setAdj] = useState(null);
    const [log, setLog] = useState(null);
    const load = () => api.get(`/admin/lse/warehouses/${wh.id}/stock`).then(({ data: d }) => setData(d)).catch((e) => toast.error(errText(e)));
    useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
    const rows = useMemo(() => (data?.stock || []).filter((r) => !q || `${r.product_name} ${r.sku || ""} ${r.brand || ""}`.toLowerCase().includes(q.toLowerCase())), [data, q]);
    const addable = useMemo(() => (data?.unstocked_products || []).filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8), [data, q]);
    const setRow = async (product_id, patch) => {
        try { await api.put(`/admin/lse/warehouses/${wh.id}/stock`, { product_id, ...patch }); load(); }
        catch (e) { toast.error(errText(e)); }
    };
    const showLog = async (r) => {
        try { const { data: d } = await api.get(`/admin/lse/warehouses/${wh.id}/stock/log?product_id=${r.product_id}`); setLog({ row: r, entries: d.log }); }
        catch (e) { toast.error(errText(e)); }
    };
    return (
        <Modal wide title={`${wh.name} · stock`} onClose={onClose}>
            <label className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2"><Search size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, SKU, brand" className="bg-transparent outline-none text-sm flex-1 min-w-0" /></label>
            {!data ? <Loading /> : (
                <>
                    <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-sm min-w-[640px]">
                            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                                <tr><th className="py-2">Product</th><th>Available</th><th>Reserved</th><th>Damaged</th><th>Sellable</th><th>Reorder at</th><th>Bin</th><th /></tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((r) => (
                                    <tr key={r.product_id} data-testid={`lse-stock-${r.product_id}`}>
                                        <td className="py-2 pr-2"><p className="font-semibold truncate max-w-[200px]">{r.product_name}</p>{r.sku && <p className="text-[11px] text-muted-foreground font-mono">{r.sku}</p>}</td>
                                        <td><NumCell value={r.available} onSave={(v) => setRow(r.product_id, { available: v })} /></td>
                                        <td className="tabular-nums">{r.reserved}</td>
                                        <td className="tabular-nums">{r.damaged || 0}</td>
                                        <td className="tabular-nums">{r.low_stock ? <Pill tone={r.sellable <= 0 ? "red" : "amber"}>{r.sellable}</Pill> : r.sellable}</td>
                                        <td><NumCell value={r.reorder_level} onSave={(v) => setRow(r.product_id, { reorder_level: v })} /></td>
                                        <td><TextCell value={r.bin_location} onSave={(v) => setRow(r.product_id, { bin_location: v })} /></td>
                                        <td className="text-right whitespace-nowrap">
                                            <button type="button" className="text-xs font-semibold text-primary px-1" onClick={() => setAdj(r)}>Adjust</button>
                                            <button type="button" className="text-xs px-1" onClick={() => showLog(r)} aria-label="History"><History size={14} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {rows.length === 0 && <p className="py-4 text-sm text-muted-foreground">No stock rows{q ? " match" : " yet"}.</p>}
                    </div>
                    {addable.length > 0 && (
                        <div className="mt-4">
                            <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Start tracking here</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {addable.map((p) => (
                                    <button key={p.id} type="button" onClick={() => setRow(p.id, { available: 0 })} className="rounded-full border border-border px-3 py-1 text-xs font-semibold inline-flex items-center gap-1">
                                        <PackagePlus size={12} /> {p.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
            {adj && <AdjustModal wh={wh} row={adj} onClose={() => setAdj(null)} onDone={() => { setAdj(null); load(); }} />}
            {log && (
                <Modal title={`${log.row.product_name} · history`} onClose={() => setLog(null)}>
                    <ul className="space-y-1.5 text-sm">
                        {log.entries.map((e) => <li key={e.id} className="flex justify-between gap-2"><span>{e.kind.replace(/_/g, " ").toLowerCase()} {e.qty > 0 ? `+${e.qty}` : e.qty}{e.note ? ` · ${e.note}` : ""}</span><span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</span></li>)}
                        {log.entries.length === 0 && <li className="text-muted-foreground">No movements yet.</li>}
                    </ul>
                </Modal>
            )}
        </Modal>
    );
}

function NumCell({ value, onSave }) {
    const [v, setV] = useState(value ?? "");
    useEffect(() => setV(value ?? ""), [value]);
    return <input className="w-20 rounded-lg bg-muted px-2 py-1 text-sm tabular-nums" type="number" min="0" value={v} onChange={(e) => setV(e.target.value)}
                  onBlur={() => { if (String(v) !== String(value ?? "") && v !== "") onSave(Number(v)); }} />;
}

function TextCell({ value, onSave }) {
    const [v, setV] = useState(value ?? "");
    useEffect(() => setV(value ?? ""), [value]);
    return <input className="w-20 rounded-lg bg-muted px-2 py-1 text-sm" maxLength={40} value={v} onChange={(e) => setV(e.target.value)}
                  onBlur={() => { if (v !== (value ?? "")) onSave(v); }} placeholder="A-01" />;
}

function AdjustModal({ wh, row, onClose, onDone }) {
    const [kind, setKind] = useState("DAMAGE");
    const [qty, setQty] = useState(1);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try { await api.post(`/admin/lse/warehouses/${wh.id}/stock/adjust`, { product_id: row.product_id, kind, qty: Number(qty), note: note || null }); toast.success("Stock updated"); onDone(); }
        catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
    };
    return (
        <Modal title={`Adjust · ${row.product_name}`} onClose={onClose} footer={<><Btn tone="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy || !(qty > 0)}>Apply</Btn></>}>
            <div className="grid gap-3">
                <Field label="What happened">
                    <select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value)}>
                        <option value="RECEIVE">Received new stock</option>
                        <option value="DAMAGE">Damaged (move to damaged)</option>
                        <option value="WRITE_OFF_DAMAGED">Write off damaged stock</option>
                        <option value="RESTORE_DAMAGED">Damaged stock is sellable again</option>
                        <option value="CORRECTION">Count correction (remove)</option>
                    </select>
                </Field>
                <Field label="Quantity"><input className={inputCls} type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
                <Field label="Note"><input className={inputCls} maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
            </div>
        </Modal>
    );
}
